import { Modals } from "../../../types/enums";
import "./reward-group-items.css";

import EventEmitter from "eventemitter3";
import { Modal } from "../modal";
import { Data } from "../../../models/data";
import { Context } from "../../../models/context";
import { LocalizationService } from "../../../services/localization-service";
import { DataLayerService } from "../../../services/data-layer-service";
import { EventTypes } from "../../../core/event-bus";
import { MenuItemCard } from "../../shared/MenuItemCard/menu-item-card";
import { IDataItem, IDataLoyaltyItem, IDataRedeemItemDisplayGroup } from "../../../models/data.interface";
import { Order } from "../../../models/order";
import { Util } from "../../../utils/util";

/**
 * A modal dialog for selecting the second half of a meal deal.
 */
export class RewardGroupItems extends Modal {
    private readonly _data: Data;
    private readonly _order: Order;
    private readonly _itemsElement: HTMLElement | null;
    private readonly _noItemsElement: HTMLElement | null;
    private readonly _headerExtraInfoElement: HTMLElement | null;
    private _options?: IRewardGroupItemsProps;

    /**
     * Initializes a new instance of the SelectSecondHalfModal class.
     * @param eventBus The event bus instance.
     * @param data The data service instance.
     * @param order The order service instance.
     * @param context The context instance.
     * @param localizationService The localization service instance.
     * @param dataLayer The data layer service instance.
     */
    constructor(
        eventBus: EventEmitter,
        data: Data,
        order: Order,
        context: Context,
        localizationService: LocalizationService,
        dataLayer: DataLayerService,
    ) {
        super(
            "modal_reward_group_items",
            eventBus,
            context,
            dataLayer,
            localizationService,
            {
                hasCloseButton: true,
                ariaLabel: "Reward Group Items"
            },
            Modals.REWARD_GROUP_ITEMS,
        );
        this._data = data;
        this._itemsElement = document.getElementById("div_reward_group_items") ?? null;
        this._noItemsElement = document.getElementById("div_reward_group_items_no_items") ?? null;
        this._headerExtraInfoElement = document.getElementById("div_reward_group_items_header") ?? null;
        this._order = order;

        this._eventBusListeners();
    }

    protected readonly _setEventListeners = (): void => {};

    /**
     * Subscribes to events on the event bus.
     * @private
     */
    private readonly _eventBusListeners = () => {
        this._eventBus.on(EventTypes.MODAL_OPEN, (modal: Modals, options?: IRewardGroupItemsProps) => {
            if (modal === Modals.REWARD_GROUP_ITEMS) this._options = options;
        });

        this._eventBus.on(EventTypes.DATA_RETRIEVED, this._renderContent);
    };

    /**
     * Opens the modal dialog.
     * @public
     */
    public override open(): void {
        super.init();
        this._renderHeader();
        this._renderContent();
        super.open();
    }

    private _renderHeader(): void {
        this._setHeaderPoints();
        this._setHeaderGroupName();
    }

    private _setHeaderPoints() {
        const points = this._options?.loyaltyPoints ?? 0;

        const pointsText = this._localizationService
            .names("AVAILABLE_POINTS_LABEL_WITH_POINTS")
            .replaceAll("{{points}}", points.toLocaleString(this._context.getProperty("languageCode")))
            .replaceAll("{{points_unit}}", this._getPointsUnitText())

        const pointsElement = document.getElementById("div_reward_group_items_header_points");
        if (pointsElement) {
            pointsElement.innerHTML = `
                <span class="icon-coin"></span>
                <span>${pointsText}</span>
            `;

        }
    }

    private _setHeaderGroupName() {
        const groupName = this._localizationService.GetName(this._options?.redeemItemDisplayGroup.Names ?? []);

        const groupNameText = this._localizationService
            .names("RewardGroupItemsHeader")
            .replace("{{ reward_group_name }}", groupName);

        const groupNameElement = document.getElementById("div_reward_group_items_header_group_name");
        if (groupNameElement) groupNameElement.textContent = groupNameText;
    }

    /**
     * Renders the modal content.
     * @private
     */
    private readonly _renderContent = (): void => {
        this._hideElements();

        const items = this._getEligibleItems();

        if (items.length === 0) {
            this._displayNoItemsMessage();
            return;
        }

        this._displayItems(items);
    };

    private _hideElements(): void {
        if (this._itemsElement) Util.hideElement(this._itemsElement);
        if (this._noItemsElement) Util.hideElement(this._noItemsElement);
    }

    private _displayNoItemsMessage(): void {
        if (!this._noItemsElement) return;

        Util.showElement(this._noItemsElement);
    }

    private _displayItems(items: { dataItem: IDataItem; groupItem: IDataLoyaltyItem }[]): void {
        if (!this._itemsElement) return;

        this._itemsElement.innerHTML = "";

        for (const item of items) {
            const itemCard = new MenuItemCard(
                this._localizationService,
                item.dataItem,
                () => {
                    this._handleMenuItemOnClick(item.dataItem);
                },
                {
                    pointsText: this._createPointsText(item.groupItem),
                    sizeText: this._createSizeText(item.groupItem),
                    locked: (this._options?.loyaltyPoints ?? 0) < item.groupItem.Points,
                },
            );
            this._itemsElement.appendChild(itemCard.element);
        }
        Util.showElement(this._itemsElement);
    }

    private _createPointsText(groupItem: IDataLoyaltyItem): string {
        const pointsText = groupItem.Points.toLocaleString(this._context.getProperty("languageCode"));

        const pointsFullText = this._localizationService.names("POINTS_LABEL_WITH_POINTS")
            .replaceAll("{{points}}", pointsText)
            .replaceAll("{{points_unit}}", this._getPointsUnitText());

        return pointsFullText;
    }

    private _createSizeText(groupItem: IDataLoyaltyItem): string {
        const size = this._data.getProperty("Sizes")?.find((size) => size.SizeId === groupItem.SizeId);

        const sizeText = this._localizationService.GetName(size?.Names ?? []);

        return sizeText;
    }

    /**
     * Gets the eligible items for the redeem group.
     * @private
     * @returns {IDataItem[]} The eligible items.
     */
    private _getEligibleItems(): { dataItem: IDataItem; groupItem: IDataLoyaltyItem }[] {
        const groupItems = this._data
            .getBrandProperty("RedeemItemDisplayGroups")
            ?.find(
                (group) =>
                    group.LoyaltyItemDisplayGroupID ===
                    this._options?.redeemItemDisplayGroup.LoyaltyItemDisplayGroupID,
            )?.Items;

        if (!groupItems || groupItems.length === 0) return [];

        const eligibleItems: { dataItem: IDataItem; groupItem: IDataLoyaltyItem }[] = [];
        const orderTypeId = this._order.getProperty("OrderTypeID");

        for (const groupItem of groupItems) {
            const item = this._data
                .getProperty("Items")
                .find(
                    (dataItem) =>
                        dataItem.ItemId === groupItem.ItemId &&
                        dataItem.OrderTypes.some(
                            (ot) =>
                                ot.OrderTypeId === orderTypeId &&
                                ot.Sizes.some((size) => size.SizeId === groupItem.SizeId),
                        ),
                );
            if (item) {
                eligibleItems.push({ dataItem: item, groupItem });
            }
        }

        return eligibleItems;
    }

    private _handleMenuItemOnClick(menuItem: IDataItem): void {
        return;
    }

    /**
     * Returns the localized loyalty points unit label.
     * @returns {string} The localized loyalty unit name.
     */
    private _getPointsUnitText(): string {
        const loyaltyInfo = this._data.getLoyaltyInfo();
        return this._localizationService.getLoyaltyUnitName(loyaltyInfo);
    }
}

export interface IRewardGroupItemsProps {
    redeemItemDisplayGroup: IDataRedeemItemDisplayGroup;
    loyaltyPoints: number;
}
